import { z } from 'zod'

import { objectSchema, schemaModel } from '@scalar-org/entities/utility'
import type { DocumentPrimitive, FieldQuery } from '@scalar-org/ts-helpers'

// Mirrored from mongobase/domains/database/api/routes
type Response<T> = {
  data: T
  status: number
}

const primitiveSchema: z.ZodType<DocumentPrimitive> = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
])

const $filterSchema: z.ZodType<FieldQuery> = z.object({
  $in: z.string().array().optional(),
  $nin: z.string().array().optional(),
  $eq: primitiveSchema.optional(),
  $ne: primitiveSchema.optional(),
  $lt: z.number().optional(),
  $gt: z.number().optional(),
  $gte: z.number().optional(),
  $lte: z.number().optional(),
  $or: z.lazy(() => $filterSchema.array()).optional(),
})

type MongobaseQuery = Record<string, FieldQuery | DocumentPrimitive>

const querySchema = z.record(
  z.string(),
  z.union([primitiveSchema, $filterSchema]),
)

const getRecordRequestSchema = z.object({
  collectionPath: z.string(),
  query: querySchema,
})

const addRecordRequestSchema = z.object({
  collectionPath: z.string(),
  data: objectSchema,
})

const addMultipleRecordsRequestSchema = z.object({
  collectionPath: z.string(),
  data: objectSchema.array(),
})

const updateRecordRequestSchema = z.object({
  collectionPath: z.string(),
  data: objectSchema,
  query: querySchema,
})

const deleteRecordRequestSchema = z.object({
  collectionPath: z.string(),
  query: querySchema,
})

const dropCollectionRequestSchema = z.object({
  collectionPath: z.string(),
})

// ---------------------------------------------------------------------------

type DBResponse<T> = {
  status: number
  data: T
  error: false
}

function formatResponse<T>(
  response: Response<any>,
  schema: z.ZodType<T>,
): DBResponse<T> {
  return {
    status: response.status,
    data: schemaModel(response.data, schema),
    error: false,
  }
}

type DBError = {
  status: number
  message: string
  error: true
  originalError: any
}

const statusMessages: Record<string | number, string> = {
  401: 'Unauthorized. Please check that an access token was provided to the instance. ',
}

function formatError(error: any, message?: string): DBError {
  return {
    status: error.status ?? 500,
    message:
      statusMessages[error.response?.status as number] ??
      error.message ??
      message,
    error: true,
    originalError: error,
  }
}

const addResponseSchema = z.object({
  insertedId: z.string(),
})

const addMultiResponseSchema = z.object({
  insertedCount: z.number(),
  insertedIds: z.preprocess(
    (s) => (Array.isArray(s) ? s : Object.values(s as any)),
    z.string().array(),
  ),
})

const updateResponseSchema = z.object({
  matchedCount: z.number(),
  modifiedCount: z.number(),
  upsertedCount: z.number(),
  upsertedId: z.string().nullable(),
})

const deletedResponseSchema = z.object({
  deletedCount: z.number(),
})

// ---------------------------------------------------------------------------

/** Initialize database access */
export function createAccess(baseURL: string) {
  const state = {
    accessToken: '',
  }

  /** Request sending instance */
  function makeRequest<T>(options: {
    url: string
    method: string
    headers?: Record<string, string>
    data?: any
    schema: z.ZodType<T>
  }): Promise<DBResponse<T> | DBError> {
    return fetch(`${baseURL}${options.url}`, {
      method: options.method,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${state.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options.data),
    })
      .then(async (res) => {
        if (!res.ok) {
          return formatError(
            { status: res.status, message: await res.text() },
            'Unknown Error',
          )
        }

        return formatResponse(
          { data: await res.json(), status: res.status },
          options.schema,
        )
      })
      .catch((err) => formatError(err))
  }

  /** Get one or more items from the database */
  function get<T>({
    collectionPath,
    query,
    schema,
    type,
  }: {
    collectionPath: string
    query: MongobaseQuery
    schema: z.ZodType<T>
    type: 'one' | 'many'
  }) {
    const data = schemaModel({ collectionPath, query }, getRecordRequestSchema)

    return makeRequest({
      url: `/database/get-${type}`,
      method: 'POST',
      data,
      schema,
    })
  }

  /** Add one or more records to the database */
  function addOne<T extends Record<string, any>, R>({
    collectionPath,
    data,
    schema,
    responseSchema,
  }: {
    collectionPath: string
    data: T
    schema: z.ZodType<T>
    responseSchema: z.ZodType<R>
  }) {
    const sanitizedData = schemaModel(
      { collectionPath, data: schemaModel(data, schema) },
      addRecordRequestSchema,
    )

    return makeRequest({
      url: '/database/add-one',
      method: 'POST',
      data: sanitizedData,
      schema: responseSchema,
    })
  }

  /** Add multiple records to the database */
  function addMultiple<T extends Record<string, any>[], R>({
    collectionPath,
    data,
    schema,
    responseSchema,
  }: {
    collectionPath: string
    data: T
    schema: z.ZodType<T>
    responseSchema: z.ZodType<R>
  }) {
    const sanitizedData = schemaModel(
      { collectionPath, data: schemaModel(data, schema) },
      addMultipleRecordsRequestSchema,
    )

    return makeRequest({
      url: '/database/add-many',
      method: 'POST',
      data: sanitizedData,
      schema: responseSchema,
    })
  }

  function remove({
    collectionPath,
    query,
    type,
  }: {
    collectionPath: string
    query: MongobaseQuery
    type: 'one' | 'many'
  }) {
    const data = schemaModel(
      { collectionPath, query },
      deleteRecordRequestSchema,
    )

    return makeRequest({
      url: `/database/delete-${type}`,
      method: 'POST',
      data,
      schema: deletedResponseSchema,
    })
  }

  function update<T>({
    collectionPath,
    data,
    query,
    schema,
  }: {
    collectionPath: string
    data: Partial<T>
    query: MongobaseQuery
    schema: z.ZodType<Partial<T>>
  }) {
    const updateData = schemaModel(
      {
        collectionPath,
        query,
        data: schemaModel(data, schema),
      },
      updateRecordRequestSchema,
    )

    return makeRequest({
      url: '/database/update-one',
      method: 'POST',
      data: updateData,
      schema: updateResponseSchema,
    })
  }

  function dropCollection({ collectionPath }: { collectionPath: string }) {
    const data = schemaModel({ collectionPath }, dropCollectionRequestSchema)

    return makeRequest({
      url: '/database/drop-collection',
      method: 'POST',
      data,
      schema: z.any(),
    })
  }
  // ---------------------------------------------------------------------------
  /** Return the database access interface */
  return {
    /** Update the access token */
    updateAccessToken(accessToken: string) {
      state.accessToken = accessToken
    },

    async checkHealth(url: string) {
      // For not access token we just return a mock error
      if (!state.accessToken) {
        return {
          status: 511,
          message: 'No Access Token',
          error: true,
          originalError: null,
        }
      }

      return await makeRequest({
        url,
        method: 'GET',
        schema: z.any(),
      })
    },

    /** Get a single record */
    getOne<T>(options: {
      collectionPath: string
      query: MongobaseQuery
      schema: z.ZodType<T>
    }) {
      return get({ ...options, type: 'one' })
    },

    /** Get multiple records */
    getMany<T>(options: {
      collectionPath: string
      query: MongobaseQuery
      schema: z.ZodType<Array<T>>
    }) {
      return get({ ...options, type: 'many' })
    },

    /** Add a single record */
    addOne<T extends Record<string, any>>(options: {
      collectionPath: string
      data: T
      schema: z.ZodType<T>
    }) {
      return addOne({ ...options, responseSchema: addResponseSchema })
    },

    /** Add multiple records */
    addMany<T extends Record<string, any>>(options: {
      collectionPath: string
      data: Array<T>
      schema: z.ZodType<Array<T>>
    }) {
      return addMultiple({ ...options, responseSchema: addMultiResponseSchema })
    },

    updateOne<T>(options: {
      collectionPath: string
      data: Partial<T>
      query: MongobaseQuery
      schema: z.ZodType<Partial<T>>
    }) {
      return update({ ...options })
    },

    /** Delete a single record */
    deleteOne(options: { collectionPath: string; query: MongobaseQuery }) {
      return remove({ ...options, type: 'one' })
    },

    /** Delete multiple records */
    deleteMany(options: { collectionPath: string; query: MongobaseQuery }) {
      return remove({ ...options, type: 'many' })
    },

    dropCollection,
  }
}
